import {
  SOUND_CATEGORIES,
  type SoundCategory,
} from "./SoundCategory.js";
import { SoundCatalog } from "./SoundCatalog.js";
import { VolumeMixer } from "./VolumeMixer.js";
import type {
  SoundPlayer,
  SoundPlayHandle,
} from "./SoundPlayer.js";

export interface SoundManagerOptions {
  initialMaster?: number;
  initialMasterMuted?: boolean;
  initialGroupVolumes?: Partial<Record<SoundCategory, number>>;
  initialGroupsMuted?: Partial<Record<SoundCategory, boolean>>;
  /** missing id 호출 시 throw할지 silent warn할지. default false. */
  throwOnMissing?: boolean;
}

/**
 * 사운드 시스템 facade — Catalog·VolumeMixer·SoundPlayer를 묶어
 * 호출자에게 단일 API(`play`/`playBgm`/`stop`/`stopAll`/볼륨·뮤트 setter)를 제공.
 *
 * 책임:
 * 1. id → `SoundDefinition` 조회 (catalog 미존재 id는 silent warn + null 반환, throwOnMissing=true 시 throw)
 * 2. 볼륨 계산 위임 (mixer)
 * 3. 활성 handle 추적 — master/group 볼륨 변경 시 일괄 `setVolume` 호출
 * 4. BGM 단일 슬롯 관리 (`playBgm`이 기존 BGM 정지 후 새 트랙)
 * 5. AudioContext autoplay 정책 우회 — `unlockOnFirstGesture`로 첫 사용자 제스처 시 unlock + 보류 BGM 재생
 */
export class SoundManager {
  private readonly catalog: SoundCatalog;
  private readonly player: SoundPlayer;
  private readonly mixer: VolumeMixer;
  private readonly throwOnMissing: boolean;
  private readonly activeByCategory = new Map<SoundCategory, Set<SoundPlayHandle>>();
  private readonly activeByDefinition = new Map<string, Set<SoundPlayHandle>>();
  private currentBgmHandle: SoundPlayHandle | null = null;
  private pendingBgmId: string | null = null;
  private unlocked = false;
  private unlockCleanup: (() => void) | null = null;

  constructor(
    catalog: SoundCatalog,
    player: SoundPlayer,
    options: SoundManagerOptions = {},
  ) {
    this.catalog = catalog;
    this.player = player;
    this.mixer = new VolumeMixer({
      master: options.initialMaster,
      masterMuted: options.initialMasterMuted,
      groups: options.initialGroupVolumes,
      groupsMuted: options.initialGroupsMuted,
    });
    this.throwOnMissing = options.throwOnMissing ?? false;
    for (const cat of SOUND_CATEGORIES) {
      this.activeByCategory.set(cat, new Set());
    }
  }

  // ─ 글로벌 ─────────────────────────────────────────────────────────────────
  setMasterVolume(value: number): void {
    this.mixer.setMasterVolume(value);
    this.applyVolumeToAllActive();
  }
  getMasterVolume(): number {
    return this.mixer.getMasterVolume();
  }
  setMasterMuted(muted: boolean): void {
    this.mixer.setMasterMuted(muted);
    this.applyVolumeToAllActive();
  }
  isMasterMuted(): boolean {
    return this.mixer.isMasterMuted();
  }

  // ─ 그룹 ──────────────────────────────────────────────────────────────────
  setGroupVolume(category: SoundCategory, value: number): void {
    this.mixer.setGroupVolume(category, value);
    this.applyVolumeToCategory(category);
  }
  getGroupVolume(category: SoundCategory): number {
    return this.mixer.getGroupVolume(category);
  }
  setGroupMuted(category: SoundCategory, muted: boolean): void {
    this.mixer.setGroupMuted(category, muted);
    this.applyVolumeToCategory(category);
  }
  isGroupMuted(category: SoundCategory): boolean {
    return this.mixer.isGroupMuted(category);
  }

  // ─ 로컬 ──────────────────────────────────────────────────────────────────
  setLocalVolume(id: string, value: number): void {
    this.mixer.setLocalVolume(id, value);
    const def = this.catalog.get(id);
    if (def === undefined) return;
    const handles = this.activeByDefinition.get(id);
    if (handles === undefined) return;
    const eff = this.mixer.computeEffectiveVolume(def);
    for (const handle of handles) {
      this.player.setVolume(handle, eff);
    }
  }
  getLocalVolume(id: string): number {
    return this.mixer.getLocalVolume(id);
  }

  // ─ 재생 ──────────────────────────────────────────────────────────────────
  /**
   * 사운드 1회 재생. id가 catalog에 없으면 throwOnMissing 옵션에 따라 throw 또는 warn+null.
   *
   * `options.loop`은 default `def.loop ?? false`. 명시 시 정의 값을 덮어쓴다.
   */
  play(
    id: string,
    options: { loop?: boolean; pitchMultiplier?: number } = {},
  ): SoundPlayHandle | null {
    const def = this.catalog.get(id);
    if (def === undefined) return this.handleMissing(id);
    const effective = this.mixer.computeEffectiveVolume(def);
    const loop = options.loop ?? def.loop ?? false;
    let handle: SoundPlayHandle | null = null;
    handle = this.player.play(def, {
      volume: effective,
      loop,
      pitchMultiplier: options.pitchMultiplier,
      onEnded: () => {
        if (handle !== null) {
          this.untrack(def.category, def.id, handle);
          if (this.currentBgmHandle === handle) this.currentBgmHandle = null;
        }
      },
    });
    this.track(def.category, def.id, handle);
    return handle;
  }

  /**
   * BGM 단일 슬롯 헬퍼.
   * - 기존 BGM 핸들이 있으면 stop 후 새 트랙 시작.
   * - 아직 unlock되지 않았으면 (`unlockOnFirstGesture` 호출 전 또는 첫 제스처 전) 보류 → unlock 시 자동 재생.
   * - 항상 loop=true.
   */
  playBgm(id: string): SoundPlayHandle | null {
    if (this.currentBgmHandle !== null) {
      this.player.stop(this.currentBgmHandle);
      this.untrack("bgm", this.currentBgmHandle.id, this.currentBgmHandle);
      this.currentBgmHandle = null;
    }
    if (!this.unlocked) {
      this.pendingBgmId = id;
      return null;
    }
    const handle = this.play(id, { loop: true });
    this.currentBgmHandle = handle;
    return handle;
  }

  stop(handle: SoundPlayHandle): void {
    this.player.stop(handle);
    const def = this.catalog.get(handle.id);
    if (def !== undefined) {
      this.untrack(def.category, handle.id, handle);
    }
    if (this.currentBgmHandle === handle) this.currentBgmHandle = null;
  }

  /**
   * 카테고리 전체 또는(인자 미지정 시) 전부 정지.
   */
  stopAll(category?: SoundCategory): void {
    if (category === undefined) {
      this.player.stopAll();
      for (const set of this.activeByCategory.values()) set.clear();
      this.activeByDefinition.clear();
      this.currentBgmHandle = null;
      return;
    }
    const handles = this.activeByCategory.get(category);
    if (handles === undefined) return;
    for (const handle of [...handles]) {
      this.player.stop(handle);
      this.untrack(category, handle.id, handle);
    }
    if (category === "bgm") this.currentBgmHandle = null;
  }

  /**
   * 첫 사용자 제스처(click/keydown/touchstart) 1회 후 `player.unlock()` 호출 +
   * 보류된 BGM 자동 재생. 일회용 — 첫 호출 후 listener 해제.
   *
   * SSR 환경(브라우저 외)에서는 호출자가 본 메서드를 호출하지 않거나, target에 더미 EventTarget 주입.
   *
   * @param target click/keydown/touchstart 이벤트를 발화하는 EventTarget (보통 `document` 또는 root element)
   * @returns 수동 해제용 cleanup 함수
   */
  unlockOnFirstGesture(target: EventTarget): () => void {
    if (this.unlocked) return () => {};
    if (this.unlockCleanup !== null) return this.unlockCleanup;
    const events = ["click", "keydown", "touchstart"] as const;
    const handler = (): void => {
      void this.handleFirstGesture();
    };
    for (const ev of events) {
      target.addEventListener(ev, handler, { once: true } as AddEventListenerOptions);
    }
    const cleanup = (): void => {
      for (const ev of events) {
        target.removeEventListener(ev, handler);
      }
      if (this.unlockCleanup === cleanup) this.unlockCleanup = null;
    };
    this.unlockCleanup = cleanup;
    return cleanup;
  }

  private async handleFirstGesture(): Promise<void> {
    if (this.unlockCleanup !== null) {
      this.unlockCleanup();
    }
    this.unlocked = true;
    try {
      await this.player.unlock?.();
    } catch (err) {
      console.warn(`[SoundManager] unlock failed: ${(err as Error).message}`);
    }
    if (this.pendingBgmId !== null) {
      const id = this.pendingBgmId;
      this.pendingBgmId = null;
      const handle = this.play(id, { loop: true });
      this.currentBgmHandle = handle;
    }
  }

  dispose(): void {
    if (this.unlockCleanup !== null) this.unlockCleanup();
    this.player.stopAll();
    this.player.dispose();
    for (const set of this.activeByCategory.values()) set.clear();
    this.activeByDefinition.clear();
    this.currentBgmHandle = null;
    this.pendingBgmId = null;
  }

  getCatalog(): SoundCatalog {
    return this.catalog;
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  // ─ 내부 ─────────────────────────────────────────────────────────────────
  private track(category: SoundCategory, id: string, handle: SoundPlayHandle): void {
    this.activeByCategory.get(category)?.add(handle);
    let set = this.activeByDefinition.get(id);
    if (set === undefined) {
      set = new Set();
      this.activeByDefinition.set(id, set);
    }
    set.add(handle);
  }

  private untrack(category: SoundCategory, id: string, handle: SoundPlayHandle): void {
    this.activeByCategory.get(category)?.delete(handle);
    const set = this.activeByDefinition.get(id);
    if (set !== undefined) {
      set.delete(handle);
      if (set.size === 0) this.activeByDefinition.delete(id);
    }
  }

  private applyVolumeToAllActive(): void {
    for (const cat of SOUND_CATEGORIES) {
      this.applyVolumeToCategory(cat);
    }
  }

  private applyVolumeToCategory(category: SoundCategory): void {
    const handles = this.activeByCategory.get(category);
    if (handles === undefined) return;
    for (const handle of handles) {
      const def = this.catalog.get(handle.id);
      if (def === undefined) continue;
      const eff = this.mixer.computeEffectiveVolume(def);
      this.player.setVolume(handle, eff);
    }
  }

  private handleMissing(id: string): null {
    const msg = `[SoundManager] unknown sound id: ${id}`;
    if (this.throwOnMissing) throw new Error(msg);
    console.warn(msg);
    return null;
  }
}
